import { DEFAULT_BAG_PRESET, GAME_CONFIG } from '../config/gameConfig';
import { getActForRound, getRoundInAct, getRoundTarget } from '../engine/shop';
import {
  enqueueEvent,
  setRunId,
  updateAnalyticsContext,
} from '../../../services/analytics/analyticsClient';
import {
  EVENT_NAMES,
  mapShopOptionForAnalytics,
  mapTileForAnalytics,
} from '../../../services/analytics/events';
import { getStoredPlayerState, updateStoredPlayerStats } from './localStorageService';

const RECENT_SUBMIT_HISTORY_LIMIT = 30;
const TOP_WORD_PLAYS_LIMIT = 5;

const getMinimumRunPointsForRoundIndex = (roundIndex = 1) => {
  const normalizedRoundIndex = Math.max(1, Number(roundIndex) || 1);
  let minimum = 0;

  for (let index = 1; index < normalizedRoundIndex; index += 1) {
    minimum += getRoundTarget(index);
  }

  return minimum;
};

export const trackRunSessionStart = ({ storedGameState, selectedBagPreset, runId }) => {
  const resumedFromStorage = Boolean(storedGameState);

  setRunId(runId);

  updateAnalyticsContext({
    bagPreset: selectedBagPreset || DEFAULT_BAG_PRESET,
    roundIndex: 1,
    actIndex: 1,
    roundInAct: 1,
    score: 0,
    runPoints: 0,
    gamePhase: 'playing',
  });

  updateStoredPlayerStats((current) => {
    const next = {
      ...current,
      meta: {
        ...current.meta,
        lastPlayedAt: Date.now(),
      },
      runs: {
        ...current.runs,
        highestRoundReached: Math.max(current.runs.highestRoundReached, 1),
      },
    };

    if (storedGameState) {
      next.runs.resumed += 1;
    } else {
      next.runs.started += 1;
      next.runs.presetUsage = {
        ...current.runs.presetUsage,
        [selectedBagPreset]: (Number(current.runs.presetUsage?.[selectedBagPreset]) || 0) + 1,
      };
    }

    return next;
  });

  enqueueEvent(resumedFromStorage ? EVENT_NAMES.RUN_RESUMED : EVENT_NAMES.RUN_STARTED, {
    bagPreset: selectedBagPreset || DEFAULT_BAG_PRESET,
    resumedFromStorage,
  });
};

export const trackSubmitStats = (submitStatsPayload) => {
  if (!submitStatsPayload) {
    return;
  }

  const selectedBagPreset = getStoredPlayerState()?.bagPreset || DEFAULT_BAG_PRESET;

  updateStoredPlayerStats((current) => {
    const words = submitStatsPayload.scoredWords || [];
    const scoreGain = Number(submitStatsPayload.scoreGain || 0);
    const lifetimeSubmittedWords = current.scoring.lifetimeSubmittedWords + words.length;
    const lifetimeSubmitsUsed = current.scoring.lifetimeSubmitsUsed + 1;
    const lifetimeScoreFromWords = current.scoring.lifetimeScoreFromWords + scoreGain;
    const lifetimePoints = current.scoring.lifetimePoints + scoreGain;
    const averageWordScore =
      lifetimeSubmittedWords > 0
        ? Math.round((lifetimeScoreFromWords / lifetimeSubmittedWords) * 100) / 100
        : 0;
    const averageSubmitScore =
      lifetimeSubmitsUsed > 0
        ? Math.round((lifetimeScoreFromWords / lifetimeSubmitsUsed) * 100) / 100
        : 0;

    let bestWord = current.scoring.bestWord;
    let longestWord = current.words.longestWord;
    const wordFrequency = { ...current.words.frequency };

    words.forEach((entry) => {
      const normalizedWord = String(entry.word || '').toUpperCase();
      const nextWordScore = Number(entry.score || 0);

      if (normalizedWord) {
        wordFrequency[normalizedWord] = (Number(wordFrequency[normalizedWord]) || 0) + 1;

        if ((normalizedWord.length || 0) > (longestWord.length || 0)) {
          longestWord = {
            word: normalizedWord,
            length: normalizedWord.length,
          };
        }
      }

      if (nextWordScore > Number(bestWord.score || 0)) {
        bestWord = {
          word: normalizedWord,
          score: nextWordScore,
        };
      }
    });

    const bestSubmitScore = Math.max(Number(current.scoring.bestSubmit.score || 0), scoreGain);
    const bestSubmitWordCount =
      scoreGain > Number(current.scoring.bestSubmit.score || 0)
        ? words.length
        : Number(current.scoring.bestSubmit.wordCount || 0);

    const recentSubmits = [
      {
        timestamp: Date.now(),
        roundIndex: submitStatsPayload.roundIndex,
        scoreGain,
        wordCount: words.length,
        words: words.map((entry) => ({ word: entry.word, score: entry.score })),
      },
      ...(Array.isArray(current.history.recentSubmits) ? current.history.recentSubmits : []),
    ].slice(0, RECENT_SUBMIT_HISTORY_LIMIT);

    const priorTopWordPlays = Array.isArray(current.history.topWordPlays)
      ? current.history.topWordPlays
      : [];

    const newWordPlays = words
      .filter((entry) => String(entry.word || '').trim())
      .map((entry) => ({
        word: String(entry.word || '').toUpperCase(),
        score: Number(entry.score || 0),
        roundIndex: submitStatsPayload.roundIndex,
        submitScore: scoreGain,
        submitWordCount: words.length,
        timestamp: Date.now(),
      }));

    const topWordPlays = [...priorTopWordPlays, ...newWordPlays]
      .sort((a, b) => {
        const byScore = Number(b.score || 0) - Number(a.score || 0);
        if (byScore !== 0) {
          return byScore;
        }

        const byRound = Number(b.roundIndex || 0) - Number(a.roundIndex || 0);
        if (byRound !== 0) {
          return byRound;
        }

        return Number(b.timestamp || 0) - Number(a.timestamp || 0);
      })
      .slice(0, TOP_WORD_PLAYS_LIMIT);

    const priorTopLongestWordPlays = Array.isArray(current.history.topLongestWordPlays)
      ? current.history.topLongestWordPlays
      : [];

    const newLongestWordPlays = words
      .filter((entry) => String(entry.word || '').trim())
      .map((entry) => {
        const normalizedWord = String(entry.word || '').toUpperCase();
        return {
          word: normalizedWord,
          length: normalizedWord.length,
          score: Number(entry.score || 0),
          roundIndex: submitStatsPayload.roundIndex,
          timestamp: Date.now(),
        };
      });

    const topLongestWordPlays = [...priorTopLongestWordPlays, ...newLongestWordPlays]
      .sort((a, b) => {
        const byLength = Number(b.length || 0) - Number(a.length || 0);
        if (byLength !== 0) return byLength;
        const byScore = Number(b.score || 0) - Number(a.score || 0);
        if (byScore !== 0) return byScore;
        return Number(b.timestamp || 0) - Number(a.timestamp || 0);
      })
      .slice(0, TOP_WORD_PLAYS_LIMIT);

    let runs = {
      ...current.runs,
      highestRoundReached: Math.max(current.runs.highestRoundReached, submitStatsPayload.roundIndex),
      highestScoreReached: Math.max(current.runs.highestScoreReached, submitStatsPayload.nextScore),
    };

    let economy = { ...current.economy };
    const recentRuns = [...(Array.isArray(current.history.recentRuns) ? current.history.recentRuns : [])];

    if (submitStatsPayload.reachedRoundTarget) {
      economy.totalRoundsCompleted += 1;
      economy.totalTargetsReached += 1;
    }

    if (submitStatsPayload.failedRound) {
      runs = {
        ...runs,
        completed: runs.completed + 1,
        failed: runs.failed + 1,
      };

      const roundReached = Math.max(1, Number(submitStatsPayload.roundIndex) || 1);
      const minimumRunPoints = getMinimumRunPointsForRoundIndex(roundReached);
      const calculatedFinalScore = Number(
        submitStatsPayload.nextRunPoints ?? submitStatsPayload.nextScore ?? 0
      );

      recentRuns.unshift({
        timestamp: Date.now(),
        result: 'failed',
        roundReached,
        finalScore: Math.max(minimumRunPoints, calculatedFinalScore),
        bagPreset: selectedBagPreset,
      });
    }

    return {
      ...current,
      meta: {
        ...current.meta,
        lastPlayedAt: Date.now(),
      },
      runs,
      scoring: {
        ...current.scoring,
        lifetimePoints,
        lifetimeScoreFromWords,
        lifetimeSubmittedWords,
        lifetimeSubmitsUsed,
        bestWord,
        bestSubmit: {
          score: bestSubmitScore,
          wordCount: bestSubmitWordCount,
        },
        averageWordScore,
        averageSubmitScore,
      },
      economy,
      words: {
        ...current.words,
        frequency: wordFrequency,
        longestWord,
      },
      history: {
        ...current.history,
        recentSubmits,
        topWordPlays,
        topLongestWordPlays,
        recentRuns,
      },
    };
  });

  const { scoreGain, scoredWords, nextScore, nextRunPoints, roundIndex, roundTarget } = submitStatsPayload;
  const { reachedRoundTarget, failedRound, achievedVictory } = submitStatsPayload;

  const nextGamePhase = achievedVictory
    ? 'victory'
    : failedRound
    ? 'game-over'
    : reachedRoundTarget
    ? 'round-complete'
    : 'playing';

  updateAnalyticsContext({
    roundIndex,
    actIndex: getActForRound(roundIndex),
    roundInAct: getRoundInAct(roundIndex),
    score: nextScore,
    runPoints: nextRunPoints,
    gamePhase: nextGamePhase,
  });

  const roundTileTarget = submitStatsPayload.roundTileTarget ?? null;
  const tilesPlacedThisRound = submitStatsPayload.tilesPlacedThisRound ?? null;
  const tileTargetMet =
    roundTileTarget != null && tilesPlacedThisRound != null
      ? tilesPlacedThisRound >= roundTileTarget
      : null;

  enqueueEvent(EVENT_NAMES.SUBMIT_WORD, {
    scoreGain,
    tilesPlaced: submitStatsPayload.tilesPlaced ?? 0,
    wordsFormed: (scoredWords || []).map((w) => ({ word: w.word, score: w.score, length: String(w.word || '').length })),
    mainWord: submitStatsPayload.mainWord ?? null,
    roundTarget,
    roundTileTarget,
    tilesPlacedThisRound,
    tileTargetMet,
    scoreOverTarget: reachedRoundTarget ? Math.max(0, nextScore - roundTarget) : 0,
    submitsRemainingAfter: submitStatsPayload.submitsRemainingAfter ?? 0,
    reachedRoundTarget: Boolean(reachedRoundTarget),
    failedRound: Boolean(failedRound),
    achievedVictory: Boolean(achievedVictory),
  });

  if (reachedRoundTarget || failedRound || achievedVictory) {
    const result = achievedVictory ? 'victory' : reachedRoundTarget ? 'won' : 'failed';
    const actIndex = getActForRound(roundIndex);
    enqueueEvent(EVENT_NAMES.ROUND_COMPLETED, {
      result,
      roundIndex,
      actIndex,
      roundInAct: getRoundInAct(roundIndex),
      finalScore: nextScore,
      roundTarget,
      roundTileTarget,
      tilesPlaced: tilesPlacedThisRound,
      tileTargetMet,
      scoreOverTarget: result !== 'failed' ? Math.max(0, nextScore - roundTarget) : 0,
      submitsUsed: submitStatsPayload.submitsUsed ?? 0,
      discardsUsed: submitStatsPayload.discardsUsed ?? 0,
    });

    const { roundsPerAct } = GAME_CONFIG.actConfig;
    if ((result === 'won' || result === 'victory') && roundIndex % roundsPerAct === 0) {
      enqueueEvent(EVENT_NAMES.ACT_COMPLETED, {
        actIndex,
        finalRoundIndex: roundIndex,
        roundsPerAct,
        runPointsAtActEnd: nextRunPoints,
        boardClearsNext: result !== 'victory',
        result: result === 'victory' ? 'victory' : 'cleared',
      });
    }
  }
};

export const trackVictory = ({ nextRunPoints, roundIndex } = {}) => {
  const selectedBagPreset = getStoredPlayerState()?.bagPreset || DEFAULT_BAG_PRESET;

  updateStoredPlayerStats((current) => {
    const recentRuns = [...(Array.isArray(current.history.recentRuns) ? current.history.recentRuns : [])];

    recentRuns.unshift({
      timestamp: Date.now(),
      result: 'won',
      roundReached: Number(roundIndex) || GAME_CONFIG.maxRounds,
      finalScore: Number(nextRunPoints || 0),
      bagPreset: selectedBagPreset,
    });

    return {
      ...current,
      meta: {
        ...current.meta,
        lastPlayedAt: Date.now(),
      },
      runs: {
        ...current.runs,
        won: (Number(current.runs.won) || 0) + 1,
        completed: (Number(current.runs.completed) || 0) + 1,
      },
      history: {
        ...current.history,
        recentRuns,
      },
    };
  });

  enqueueEvent(EVENT_NAMES.VICTORY, {
    finalScore: Number(nextRunPoints || 0),
    roundReached: Number(roundIndex) || GAME_CONFIG.maxRounds,
    bagPreset: selectedBagPreset,
  });
};

export const trackDiscardStats = (discardData) => {
  const count = Number(discardData?.count ?? 0);

  updateStoredPlayerStats((current) => ({
    ...current,
    meta: {
      ...current.meta,
      lastPlayedAt: Date.now(),
    },
    scoring: {
      ...current.scoring,
      lifetimeDiscardsUsed: current.scoring.lifetimeDiscardsUsed + 1,
      lifetimeDiscardedTiles: current.scoring.lifetimeDiscardedTiles + count,
    },
  }));

  enqueueEvent(EVENT_NAMES.DISCARD_CONFIRMED, {
    discardedTileCount: count,
    discardedLetters: discardData?.discardedLetters ?? [],
    discardedTiles: (discardData?.discardedTiles ?? []).map((t) =>
      mapTileForAnalytics(t.letter, t.modifiers)
    ),
    discardsRemainingAfter: discardData?.discardsRemainingAfter ?? 0,
  });
};

export const trackAbandonRun = ({ roundIndex, runPoints }) => {
  const selectedBagPreset = getStoredPlayerState()?.bagPreset || DEFAULT_BAG_PRESET;

  updateStoredPlayerStats((current) => {
    const roundReached = Math.max(1, Number(roundIndex) || 1);
    const finalScore = Math.max(0, Number(runPoints) || 0);

    const recentRuns = [
      {
        timestamp: Date.now(),
        result: 'abandoned',
        roundReached,
        finalScore,
        bagPreset: selectedBagPreset,
      },
      ...(Array.isArray(current.history.recentRuns) ? current.history.recentRuns : []),
    ].slice(0, 20);

    return {
      ...current,
      meta: { ...current.meta, lastPlayedAt: Date.now() },
      runs: {
        ...current.runs,
        completed: (Number(current.runs.completed) || 0) + 1,
        abandoned: (Number(current.runs.abandoned) || 0) + 1,
        highestRoundReached: Math.max(current.runs.highestRoundReached, roundReached),
        highestScoreReached: Math.max(current.runs.highestScoreReached, finalScore),
      },
      history: { ...current.history, recentRuns },
    };
  });

  enqueueEvent(EVENT_NAMES.RUN_ABANDONED, {
    roundIndex: Number(roundIndex) || 1,
    runPoints: Number(runPoints) || 0,
  });
};

export const trackShopVisit = (visitData) => {
  updateStoredPlayerStats((current) => ({
    ...current,
    meta: {
      ...current.meta,
      lastPlayedAt: Date.now(),
    },
    economy: {
      ...current.economy,
      totalShopVisits: current.economy.totalShopVisits + 1,
    },
  }));

  if (visitData) {
    updateAnalyticsContext({
      roundIndex: visitData.roundIndex,
      actIndex: getActForRound(visitData.roundIndex),
      roundInAct: getRoundInAct(visitData.roundIndex),
      gamePhase: 'shop',
    });
    const mappedOptions = (visitData.shopOptions || []).map(mapShopOptionForAnalytics);
    enqueueEvent(EVENT_NAMES.SHOP_VISITED, {
      roundIndex: visitData.roundIndex,
      earnedShopPicks: visitData.earnedShopPicks,
      shopRerollsRemaining: visitData.shopRerollsRemaining,
      shopOptions: mappedOptions,
    });
    enqueueEvent(EVENT_NAMES.SHOP_OPTIONS_DRAWN, {
      trigger: 'visit',
      shopOptions: mappedOptions,
    });
  }
};

export const trackShopSelection = (selectionData) => {
  updateStoredPlayerStats((current) => {
    const kind = selectionData?.kind;
    const rarity = selectionData?.rarity;

    const shopSelectionsByKind = { ...current.economy.shopSelectionsByKind };
    if (kind) {
      shopSelectionsByKind[kind] = (Number(shopSelectionsByKind[kind]) || 0) + 1;
    }

    const shopSelectionsByRarity = { ...current.economy.shopSelectionsByRarity };
    if (rarity) {
      shopSelectionsByRarity[rarity] = (Number(shopSelectionsByRarity[rarity]) || 0) + 1;
    }

    return {
      ...current,
      meta: {
        ...current.meta,
        lastPlayedAt: Date.now(),
      },
      economy: {
        ...current.economy,
        totalShopSelections: current.economy.totalShopSelections + 1,
        shopSelectionsByKind,
        shopSelectionsByRarity,
      },
    };
  });

  const tileLetter = selectionData?.tileLetter ?? null;
  const tile = tileLetter != null
    ? mapTileForAnalytics(tileLetter, selectionData?.tileModifiers)
    : null;
  const tileAfter = tileLetter != null && selectionData?.tileModifiersAfter != null
    ? mapTileForAnalytics(tileLetter, selectionData.tileModifiersAfter)
    : null;
  const grantedEffects = selectionData?.grantedEffects ?? [];

  enqueueEvent(EVENT_NAMES.SHOP_OPTION_CONFIRMED, {
    kind: selectionData?.kind ?? null,
    rarity: selectionData?.rarity ?? null,
    modifierId: selectionData?.modifierId ?? null,
    sourceTileId: selectionData?.sourceTileId ?? null,
    shopPicksRemainingAfter: selectionData?.shopPicksRemainingAfter ?? 0,
    grantedEffects,
    grantedEffectTypes: grantedEffects.map((e) => e.type).filter(Boolean),
    tile,
    ...(tileAfter ? { tileAfter } : {}),
  });

  if (selectionData?.afterPickShopOptions) {
    enqueueEvent(EVENT_NAMES.SHOP_OPTIONS_DRAWN, {
      trigger: 'after-pick',
      shopOptions: selectionData.afterPickShopOptions.map(mapShopOptionForAnalytics),
    });
  }

  if ((selectionData?.shopPicksRemainingAfter ?? 1) === 0) {
    updateAnalyticsContext({ gamePhase: 'playing' });
  }
};

export const trackShopSkip = (skipData) => {
  updateStoredPlayerStats((current) => ({
    ...current,
    meta: {
      ...current.meta,
      lastPlayedAt: Date.now(),
    },
    economy: {
      ...current.economy,
      totalShopSkips: current.economy.totalShopSkips + 1,
    },
  }));

  enqueueEvent(EVENT_NAMES.SHOP_SKIPPED, {
    roundIndex: skipData?.roundIndex ?? 0,
    shopPicksRemaining: skipData?.shopPicksRemaining ?? 0,
    skippedOptions: (skipData?.skippedOptions ?? []).map(mapShopOptionForAnalytics),
  });

  if ((skipData?.shopPicksRemaining ?? 1) === 0) {
    updateAnalyticsContext({ gamePhase: 'playing' });
  }
};

export const trackShopReroll = (rerollData) => {
  const mappedNewOptions = (rerollData?.newOptions ?? []).map(mapShopOptionForAnalytics);

  enqueueEvent(EVENT_NAMES.SHOP_REROLLED, {
    rerollsRemainingAfter: rerollData?.rerollsRemainingAfter ?? 0,
    oldOptions: (rerollData?.oldOptions ?? []).map(mapShopOptionForAnalytics),
    newOptions: mappedNewOptions,
  });

  enqueueEvent(EVENT_NAMES.SHOP_OPTIONS_DRAWN, {
    trigger: 'reroll',
    shopOptions: mappedNewOptions,
  });
};
